import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Typography,
  Button,
  TextField,
  List,
  ListItem,
  ListItemText,
  Divider,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Alert,
  CircularProgress,
  Snackbar
} from '@mui/material';
import {
  Send as SendIcon,
  Message as MessageIcon,
  Person as PersonIcon,
  Warning as WarningIcon,
  Info as InfoIcon,
  Refresh as RefreshIcon
} from '@mui/icons-material';
import { styled } from '@mui/material/styles';
import { calculateAge, safeFormatDate } from '../../utils/dateUtils';
import { getPatientMessages, sendMessageToPatient } from '../../services/patientMessagingService';
import { useUserData } from '../../hooks/useUserData';

// Styled Components
const MessageContainer = styled(Box)(({ theme }) => ({
  maxHeight: '600px',
  overflow: 'hidden',
  display: 'flex',
  flexDirection: 'column'
}));

const MessagesList = styled(List)(({ theme }) => ({
  flex: 1,
  overflowY: 'auto',
  maxHeight: '400px',
  backgroundColor: theme.palette.grey[50],
  borderRadius: theme.spacing(1),
  border: `1px solid ${theme.palette.divider}`,
  marginBottom: theme.spacing(2)
}));

const MessageItem = styled(ListItem)(({ theme, isOwn }) => ({
  flexDirection: 'column',
  alignItems: isOwn ? 'flex-end' : 'flex-start',
  padding: theme.spacing(1, 2),
  '& .MuiListItemAvatar-root': {
    minWidth: 'auto',
    marginRight: theme.spacing(1)
  }
}));

const MessageBubble = styled(Box)(({ theme, isOwn, isPatient }) => ({
  backgroundColor: isPatient 
    ? theme.palette.success.main 
    : isOwn 
      ? theme.palette.primary.main 
      : theme.palette.grey[200],
  color: isPatient || isOwn ? 'white' : theme.palette.text.primary,
  padding: theme.spacing(1, 1.5),
  borderRadius: isPatient 
    ? '16px 16px 4px 16px' 
    : isOwn 
      ? '16px 16px 4px 16px' 
      : '16px 16px 16px 4px',
  width: '66.67%',
  wordBreak: 'break-word',
  marginBottom: theme.spacing(0.5)
}));

const MessageTime = styled(Typography)(({ theme }) => ({
  fontSize: '0.75rem',
  color: theme.palette.text.secondary,
  marginTop: theme.spacing(0.5)
}));

const ComposeSection = styled(Box)(({ theme }) => ({
  borderTop: `1px solid ${theme.palette.divider}`,
  padding: theme.spacing(2),
  backgroundColor: theme.palette.background.paper
}));

const PatientMessagesTab = ({ patient, currentUserId, currentUserName }) => {
  const { user, defaultFacility } = useUserData();
  const [messages, setMessages] = useState([]);
  const [newMessage, setNewMessage] = useState('');
  const [loading, setLoading] = useState(false);
  const [sending, setSending] = useState(false);
  const [showInfoDialog, setShowInfoDialog] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'info' });

  // Check if patient portal is enabled and patient is 18+
  const isPatientPortalEnabled = patient?.isPatientPortalEnabled === true;
  const patientAge = patient?.dateOfBirth ? calculateAge(patient.dateOfBirth) : null;
  const isPatientAdult = patientAge && patientAge >= 18;
  const hasFacilityAccess = defaultFacility?.id;
  const canAccessMessages = isPatientPortalEnabled && isPatientAdult && hasFacilityAccess;

  // Fetch messages using service layer
  const fetchMessages = useCallback(async () => {
    if (!canAccessMessages || !defaultFacility?.id) return;
    
    setLoading(true);
    try {
      const response = await getPatientMessages(patient.id, defaultFacility?.id, { limit: 500 });
      if (response.messages) {
        // Ensure all messages have the required fields
        const processedMessages = response.messages.map(msg => ({
          ...msg,
          direction: msg.direction || 'provider_to_patient',
          senderName: msg.senderName || msg.sender || 'Unknown',
          timestamp: msg.timestamp || msg.createdAt || new Date()
        }));
        setMessages(processedMessages);
      } else {
        setMessages([]);
      }
    } catch (error) {
      console.error('Error fetching messages:', error);
      
      // Check if it's a 404 (endpoint not found) or other error
      if (error.message.includes('404') || error.message.includes('Not Found')) {
        setSnackbar({
          open: true,
          message: 'Messaging API not yet implemented.',
          severity: 'info'
        });
        setMessages([]);
      } else {
        setSnackbar({
          open: true,
          message: 'Failed to load messages',
          severity: 'error'
        });
        setMessages([]);
      }
    } finally {
      setLoading(false);
    }
  }, [canAccessMessages, patient.id, user.id, defaultFacility?.id, currentUserName, patient?.firstName, patient?.lastName]);

  // Send message using service layer
  const sendMessage = useCallback(async () => {
    if (!newMessage.trim() || !canAccessMessages || !defaultFacility?.id) return;
    
    setSending(true);
    try {
      const messageData = {
        recipientId: patient.id,
        senderId: user.id,
        facilityId: defaultFacility.id,
        content: newMessage,
        type: 'text',
        direction: 'provider_to_patient'
      };
      
      const response = await sendMessageToPatient(messageData);
      
      if (response.message) {
        // Add the new message to local state
        setMessages(prev => [...prev, response.message]);
        setNewMessage('');
        
        setSnackbar({
          open: true,
          message: 'Message sent successfully',
          severity: 'success'
        });
      } else {
        throw new Error('Invalid response from server');
      }
    } catch (error) {
      console.error('Error sending message:', error);
      
      // Check if it's a 404 (endpoint not found) or other error
      if (error.message.includes('404') || error.message.includes('Not Found')) {
        setSnackbar({
          open: true,
          message: 'Messaging API not yet implemented.',
          severity: 'info'
        });
      } else {
        setSnackbar({
          open: true,
          message: 'Failed to send message',
          severity: 'error'
        });
      }
    } finally {
      setSending(false);
    }
  }, [newMessage, canAccessMessages, patient.id, user.id, defaultFacility?.id, currentUserName]);

  useEffect(() => {
    if (canAccessMessages) {
      fetchMessages();
    }
  }, [patient?.id, canAccessMessages, fetchMessages]);

  const handleSendMessage = (e) => {
    e.preventDefault();
    sendMessage();
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      sendMessage();
    }
  };

  const handleCloseSnackbar = () => {
    setSnackbar(prev => ({ ...prev, open: false }));
  };

  // If patient portal is not enabled or patient is under 18, show disabled state
  if (!canAccessMessages) {
    return (
      <Box sx={{ textAlign: 'center', py: 4 }}>
        <WarningIcon sx={{ fontSize: 64, color: 'warning.main', mb: 2 }} />
        <Typography variant="h6" color="warning.main" gutterBottom>
          Messages Feature Unavailable
        </Typography>
                 <Typography variant="body1" color="text.secondary" paragraph>
           {!isPatientPortalEnabled 
             ? 'Patient portal is not enabled for this account.'
             : !isPatientAdult
             ? 'Patient must be 18 years or older to access messaging.'
             : !hasFacilityAccess
             ? 'No facility access available for messaging.'
             : 'Messaging is not available for this patient.'
           }
         </Typography>
        <Button
          variant="outlined"
          startIcon={<InfoIcon />}
          onClick={() => setShowInfoDialog(true)}
          sx={{ mt: 2 }}
        >
          Learn More
        </Button>
        
        {/* Info Dialog */}
        <Dialog open={showInfoDialog} onClose={() => setShowInfoDialog(false)} maxWidth="sm" fullWidth>
          <DialogTitle>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <InfoIcon color="info" />
              Messaging Requirements
            </Box>
          </DialogTitle>
          <DialogContent>
            <Typography variant="body1" paragraph>
              To enable patient messaging, the following requirements must be met:
            </Typography>
                         <List dense>
                               <ListItem>
                  <Box sx={{ 
                    bgcolor: 'primary.main', 
                    width: 24, 
                    height: 24, 
                    borderRadius: '50%',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    mr: 2
                  }}>
                    <MessageIcon sx={{ fontSize: 16, color: 'white' }} />
                  </Box>
                  <ListItemText 
                    primary="Patient Portal Enabled" 
                    secondary="The patient portal flag must be turned on in the patient's account settings."
                  />
                </ListItem>
                <ListItem>
                  <Box sx={{ 
                    bgcolor: 'primary.main', 
                    width: 24, 
                    height: 24, 
                    borderRadius: '50%',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    mr: 2
                  }}>
                    <PersonIcon sx={{ fontSize: 16, color: 'white' }} />
                  </Box>
                  <ListItemText 
                    primary="Age Requirement" 
                    secondary="Patient must be 18 years or older to participate in secure messaging."
                  />
                </ListItem>
                <ListItem>
                  <Box sx={{ 
                    bgcolor: 'primary.main', 
                    width: 24, 
                    height: 24, 
                    borderRadius: '50%',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    mr: 2
                  }}>
                    <MessageIcon sx={{ fontSize: 16, color: 'white' }} />
                  </Box>
                  <ListItemText 
                    primary="Facility Access" 
                    secondary="You must have access to a facility to send and receive messages."
                  />
                </ListItem>
             </List>
                         <Alert severity="info" sx={{ mt: 2 }}>
               <Typography variant="body2">
                 <strong>Current Status:</strong> {patient?.firstName} {patient?.lastName} is {patientAge || 'unknown'} years old.
                 {isPatientPortalEnabled ? ' Patient portal is enabled.' : ' Patient portal is disabled.'}
                 {hasFacilityAccess ? ' Facility access is available.' : ' No facility access available.'}
               </Typography>
             </Alert>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setShowInfoDialog(false)}>Close</Button>
          </DialogActions>
        </Dialog>
      </Box>
    );
  }

  return (
    <MessageContainer>
                     <Box sx={{ mb: 2 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
            <Typography variant="h6">
              Patient Messages
            </Typography>
            <Button
              variant="outlined"
              size="small"
              startIcon={<RefreshIcon />}
              onClick={fetchMessages}
              disabled={loading}
              sx={{ minWidth: 'auto', px: 2 }}
            >
              {loading ? 'Refreshing...' : 'Refresh'}
            </Button>
          </Box>
          <Chip 
            label="Patient Portal Enabled" 
            color="success" 
            size="small" 
            sx={{ mt: 1 }}
          />
        </Box>

      {/* Messages List */}
      <MessagesList>
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
            <CircularProgress />
          </Box>
        ) : messages.length === 0 ? (
          <Box sx={{ textAlign: 'center', py: 4 }}>
            <MessageIcon sx={{ fontSize: 48, color: 'text.secondary', mb: 2 }} />
            <Typography variant="body1" color="text.secondary">
              No messages yet
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Start a conversation with your patient
            </Typography>
          </Box>
        ) : (
          messages
            .filter(message => message && message.content && typeof message.content === 'string') // Filter out invalid messages
            .map((message, index, filteredMessages) => {
              // Check if message is from patient by checking direction field
              // Default to provider message if direction is not specified
              const direction = message.direction || 'provider_to_patient';
              const isPatient = direction === 'patient_to_provider';
              const isOwn = direction === 'provider_to_patient';
              const isLast = index === filteredMessages.length - 1;
              
                             // Get sender name from message payload or fallback
               const senderName = message.senderName || message.sender || 'Unknown';
              
              // Generate a unique key for the message
              const messageKey = message.id || `msg-${index}-${Date.now()}`;
              
              return (
                <React.Fragment key={messageKey}>
                                     <MessageItem isOwn={isPatient}>
                                           <Box sx={{ 
                        width: '100%', 
                        display: 'flex', 
                        flexDirection: 'column', 
                        alignItems: isPatient ? 'flex-end' : 'flex-start' 
                      }}>
                        <Typography 
                          variant="caption" 
                          sx={{ 
                            color: 'text.primary',
                            fontSize: '0.75rem',
                            fontWeight: 500,
                            mb: 0.5,
                            textAlign: isPatient ? 'right' : 'left'
                          }}
                        >
                          {senderName}
                        </Typography>
                        <MessageBubble isOwn={isOwn} isPatient={isPatient}>
                          <Typography variant="body2">
                            {message.content}
                          </Typography>
                        </MessageBubble>
                        <MessageTime variant="caption">
                          {safeFormatDate(message.timestamp || message.createdAt || new Date(), 'MMM dd, yyyy h:mm a')}
                        </MessageTime>
                      </Box>
                   </MessageItem>
                  {!isLast && <Divider variant="middle" />}
                </React.Fragment>
              );
            })
        )}
      </MessagesList>

      {/* Compose Message Section */}
      <ComposeSection>
        <Box component="form" onSubmit={handleSendMessage}>
          <TextField
            fullWidth
            multiline
            rows={3}
            placeholder="Type your message here..."
            value={newMessage}
            onChange={(e) => setNewMessage(e.target.value)}
            onKeyPress={handleKeyPress}
            disabled={sending}
            sx={{ mb: 2 }}
          />
          <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
            <Button
              type="submit"
              variant="contained"
              startIcon={sending ? <CircularProgress size={16} /> : <SendIcon />}
              disabled={!newMessage.trim() || sending}
              sx={{ minWidth: 100 }}
            >
              {sending ? 'Sending...' : 'Send'}
            </Button>
          </Box>
        </Box>
      </ComposeSection>

      {/* Snackbar for notifications */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert 
          onClose={handleCloseSnackbar} 
          severity={snackbar.severity}
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </MessageContainer>
  );
};

export default PatientMessagesTab;
